import { Check, Pencil, Plus, Trash2 } from "lucide-react";

import { Card } from "../../components/shared/card/card.tsx";
import { Button, ResponsiveDataTable, Spinner } from "../../components/ui/index.ts";
import type { ResponsiveColumn } from "../../components/ui/index.ts";
import type { Workstream } from "./types.ts";
import styles from "./workstream-table.module.css";

type WorkstreamRow = Workstream & Record<string, unknown>;

interface WorkstreamTableProps {
  workstreams: Workstream[];
  loading: boolean;
  error: string | null;
  showActions: boolean;
  onAdd: () => void;
  onToggleActions: () => void;
  onEdit: (workstream: Workstream) => void;
  onDelete: (workstream: Workstream) => void;
}

export function WorkstreamTable({
  workstreams,
  loading,
  error,
  showActions,
  onAdd,
  onToggleActions,
  onEdit,
  onDelete,
}: WorkstreamTableProps) {
  const columns: ResponsiveColumn<WorkstreamRow>[] = [
    {
      id: "workstream",
      header: "Work Stream",
      accessor: "workstream",
      sortable: true,
      truncate: true,

    },
    {
      id: "description",
      header: "Description",
      accessor: "description",
      truncate: true,
    },
    ...(showActions
      ? [{
        id: "actions",
        header: "Actions",
        nowrap: true,
        render: (row: WorkstreamRow) => (
          <div className={styles.actions}>
            <Button
              className={styles.actionButton}
              size="sm"
              variant="accent-ghost"
              startIcon={<Pencil size={16} aria-hidden />}
              onClick={() => onEdit(row)}
            >
              Edit
            </Button>
            <Button
              className={styles.actionButton}
              size="sm"
              variant="danger-ghost"
              startIcon={<Trash2 size={16} aria-hidden />}
              onClick={() => onDelete(row)}
            >
              Delete
            </Button>
          </div>
        ),
      }]
      : []),
  ];

  return (
    <div
      className={`${styles.card} ${showActions ? styles.actionsVisible : ""}`}
    >
      <div className={styles.header}>
        <h2 className="heading-lg">Work Streams</h2>
        <div className={styles.headerActions}>
          {workstreams.length > 0 && (
            <Button
              variant="light-grey"
              startIcon={
                showActions ? (
                  <Check size={18} aria-hidden />
                ) : (
                  <Pencil size={18} aria-hidden />
                )
              }
              onClick={onToggleActions}
            >
              {showActions ? "Done" : "Edit"}
            </Button>
          )}

          <Button startIcon={<Plus size={18} aria-hidden />} onClick={onAdd}>
            Add Work Stream
          </Button>
        </div>
      </div>
      {loading ? (
        <div className={styles.state}><Spinner variant="inline" /><span>Loading work streams...</span></div>
      ) : error ? (
        <div className={`${styles.state} ${styles.error}`} role="alert">{error}</div>
      ) : (
        <ResponsiveDataTable<WorkstreamRow>
          aria-label="Project work streams"
          columns={columns}
          data={workstreams as WorkstreamRow[]}
          serialNumber
          pagination={false}
          emptyState={<div className={styles.state}>No work streams found for this project.</div>}
        />
      )}
    </div>
  );
}
